import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

export default function WelcomeModal() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check if user has seen the modal before
    const hasSeenModal = localStorage.getItem('hasSeenWelcomeModal');
    
    if (!hasSeenModal) {
      // Show modal after a brief delay for better UX
      setTimeout(() => {
        setIsOpen(true);
      }, 1000);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem('hasSeenWelcomeModal', 'true');
    setIsOpen(false);
  };

  const handleWrongPlace = () => {
    // User clicked "Oops, wrong place!" - could redirect or just close
    localStorage.setItem('hasSeenWelcomeModal', 'true');
    window.open('https://www.google.com', '_blank');
    setIsOpen(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={handleClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="bg-gradient-to-br from-green-50 to-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="relative w-24 h-24">
                  {/* Prohibition sign */}
                  <svg
                    viewBox="0 0 100 100"
                    className="w-full h-full"
                  >
                    {/* Bottle icon */}
                    <g transform="translate(50, 50)">
                      <path
                        d="M-8,-25 L-8,-15 L-12,-10 L-12,20 Q-12,25 -7,25 L7,25 Q12,25 12,20 L12,-10 L8,-15 L8,-25 Z"
                        fill="#1f2937"
                      />
                      <ellipse cx="0" cy="-25" rx="8" ry="3" fill="#1f2937" />
                      <circle cx="0" cy="5" r="5" fill="white" opacity="0.3" />
                    </g>
                    {/* Red prohibition circle */}
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="#ef4444"
                      strokeWidth="8"
                    />
                    {/* Red diagonal line */}
                    <line
                      x1="20"
                      y1="20"
                      x2="80"
                      y2="80"
                      stroke="#ef4444"
                      strokeWidth="8"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>

              {/* Heading */}
              <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
                Friendly reminder!
              </h2>

              {/* Message */}
              <p className="text-lg text-gray-700 text-center mb-8 leading-relaxed">
                Just to avoid any confusion, <span className="font-semibold">ALTX</span> is dedicated to digital real estate investments, not gambling.
              </p>

              {/* CTA Button */}
              <Button
                onClick={handleClose}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-6 text-lg rounded-xl shadow-lg mb-4"
              >
                Let's invest!
              </Button>

              {/* Wrong place link */}
              <button
                onClick={handleWrongPlace}
                className="w-full text-green-600 hover:text-green-700 font-semibold text-lg transition-colors"
              >
                Oops, wrong place!
              </button>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
